import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  createEmbedKey,
  revokeEmbedKey,
  updateEmbedKeySettings,
} from '@/services/external-chat/embed-keys';
import { updateExternalChatConfig } from '@/services/external-chat/config';
import {
  activateInstructionVersion,
  createInstructionVersion,
  deleteInstructionVersion,
} from '@/services/external-chat/versions';
import { unwrap } from '@/services/external-chat/detail-error';
import { fetchSearchQueryCsv } from '@/services/external-chat/export-search-queries';
import type {
  EmbedKeySettings,
  ExternalChatConfig,
} from '@/services/external-chat/types';
import { embedKeysQueryKey } from '@/hooks/queries/external-chat/use-embed-keys';
import { externalChatConfigQueryKey } from '@/hooks/queries/external-chat/use-external-chat-config';
import { instructionVersionsQueryKey } from '@/hooks/queries/external-chat/use-instruction-versions';

const useInvalidateKeys = (orgId?: string, collectionId?: string) => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: embedKeysQueryKey(orgId, collectionId),
    });
};

const useInvalidateConfig = (orgId?: string, collectionId?: string) => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: externalChatConfigQueryKey(orgId, collectionId),
    });
};

const useInvalidateVersions = (orgId?: string, collectionId?: string) => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: instructionVersionsQueryKey(orgId, collectionId),
    });
};

export const useCreateEmbedKey = (orgId?: string, collectionId?: string) => {
  const invalidate = useInvalidateKeys(orgId, collectionId);
  return useMutation({
    mutationFn: async () => unwrap(await createEmbedKey(collectionId!)),
    onSuccess: invalidate,
  });
};

export const useRevokeEmbedKey = (orgId?: string, collectionId?: string) => {
  const invalidate = useInvalidateKeys(orgId, collectionId);
  return useMutation({
    mutationFn: async (apiKey: string) =>
      unwrap(await revokeEmbedKey(collectionId!, apiKey)),
    onSuccess: invalidate,
  });
};

/** Per-key security settings (Origin allowlist, rate limit). Omit a field to
 *  leave it untouched; send `null` to clear it back to the default. */
export const useUpdateEmbedKeySettings = (
  orgId?: string,
  collectionId?: string,
) => {
  const invalidate = useInvalidateKeys(orgId, collectionId);
  return useMutation({
    mutationFn: async ({
      apiKey,
      settings,
    }: {
      apiKey: string;
      settings: EmbedKeySettings;
    }) => unwrap(await updateEmbedKeySettings(collectionId!, apiKey, settings)),
    onSuccess: invalidate,
  });
};

export const useSaveExternalChatConfig = (
  orgId?: string,
  collectionId?: string,
) => {
  const invalidate = useInvalidateConfig(orgId, collectionId);
  return useMutation({
    mutationFn: async (config: Partial<ExternalChatConfig>) =>
      unwrap(await updateExternalChatConfig(collectionId!, config)),
    onSuccess: invalidate,
  });
};

export const useCreateInstructionVersion = (
  orgId?: string,
  collectionId?: string,
) => {
  const invalidate = useInvalidateVersions(orgId, collectionId);
  return useMutation({
    mutationFn: async (params: { prompt: string; name?: string }) =>
      unwrap(await createInstructionVersion(collectionId!, params)),
    onSuccess: invalidate,
  });
};

/**
 * Activation rewrites `custom_instructions` server-side, so both the versions
 * list and the config query go stale. Callers still apply the returned
 * instructions to the editor draft directly — a dirty draft suppresses
 * re-seeding from the refetch, and activation is confirmed precisely when the
 * draft is dirty.
 */
export const useActivateInstructionVersion = (
  orgId?: string,
  collectionId?: string,
) => {
  const invalidateVersions = useInvalidateVersions(orgId, collectionId);
  const invalidateConfig = useInvalidateConfig(orgId, collectionId);
  return useMutation({
    mutationFn: async (promptId: string) =>
      unwrap(await activateInstructionVersion(collectionId!, promptId)),
    onSuccess: async () => {
      await invalidateVersions();
      await invalidateConfig();
    },
  });
};

export const useExportSearchQueries = () =>
  useMutation({
    mutationFn: async (args: {
      orgId: string;
      collectionId: string;
      from: string;
      to: string;
    }) => unwrap(await fetchSearchQueryCsv(args)),
  });

export const useDeleteInstructionVersion = (
  orgId?: string,
  collectionId?: string,
) => {
  const invalidate = useInvalidateVersions(orgId, collectionId);
  return useMutation({
    mutationFn: async (promptId: string) =>
      unwrap(await deleteInstructionVersion(collectionId!, promptId)),
    onSuccess: invalidate,
  });
};
